import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, AlertCircle, Plus, Trash2, Building2, Link2 } from 'lucide-react';
import { ExternalVendor, PackageIdentifierMapping } from '../../components/services/PackageManager';

interface EditExternalVendorsModalProps {
  show: boolean;
  mapping: PackageIdentifierMapping | null;
  saving: boolean;
  onClose: () => void;
  onSave: (packageIdentifier: string, externalVendors: ExternalVendor[] | null) => Promise<void>;
}

const makeEmptyVendor = (): ExternalVendor => ({
  vendorName: '',
  softwareName: '',
});

const EditExternalVendorsModal: React.FC<EditExternalVendorsModalProps> = ({ show, mapping, saving, onClose, onSave }) => {
  const [externalVendors, setExternalVendors] = useState<ExternalVendor[]>([makeEmptyVendor()]);
  const [sendAsNull, setSendAsNull] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!mapping) return;

    const initialVendors = Array.isArray(mapping.externalVendors)
      ? mapping.externalVendors.map((item) => ({
          vendorName: item.vendorName || '',
          softwareName: item.softwareName || '',
        }))
      : [];

    if (initialVendors.length > 0) {
      setExternalVendors(initialVendors);
      setSendAsNull(false);
    } else {
      setExternalVendors([makeEmptyVendor()]);
      setSendAsNull(mapping.externalVendors === null);
    }

    setError('');
  }, [mapping]);

  const normalizedPackageIdentifier = useMemo(() => mapping?.packageIdentifier?.trim() || '', [mapping]);

  if (!show || !mapping) return null;

  const updateVendor = (index: number, key: keyof ExternalVendor, value: string) => {
    setExternalVendors((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)));
    setError('');
  };

  const addVendorRow = () => {
    setExternalVendors((prev) => [...prev, makeEmptyVendor()]);
    setSendAsNull(false);
    setError('');
  };

  const removeVendorRow = (index: number) => {
    setExternalVendors((prev) => {
      if (prev.length === 1) {
        return [makeEmptyVendor()];
      }
      return prev.filter((_, idx) => idx !== index);
    });
    setError('');
  };

  const handleSave = async () => {
    if (!normalizedPackageIdentifier) {
      setError('Package Identifier is required to update external vendors.');
      return;
    }

    if (sendAsNull) {
      setError('');
      await onSave(normalizedPackageIdentifier, null);
      return;
    }

    const cleaned: ExternalVendor[] = [];

    for (const entry of externalVendors) {
      const vendorName = entry.vendorName.trim();
      const softwareName = entry.softwareName.trim();

      if (!vendorName && !softwareName) {
        continue;
      }

      if (!vendorName || !softwareName) {
        setError('Both Vendor Name and Software Name are required for each vendor row.');
        return;
      }

      cleaned.push({ vendorName, softwareName });
    }

    const dedupedMap = new Map<string, ExternalVendor>();
    cleaned.forEach((item) => {
      const key = `${item.vendorName.toLowerCase()}::${item.softwareName.toLowerCase()}`;
      if (!dedupedMap.has(key)) {
        dedupedMap.set(key, item);
      }
    });

    const deduped = Array.from(dedupedMap.values());

    setError('');
    await onSave(normalizedPackageIdentifier, deduped.length > 0 ? deduped : null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity animate-fade-in">
      <div className="bg-gradient-to-br from-[#1c1c1c] to-[#2a2a2a] text-white rounded-2xl shadow-2xl p-6 w-[760px] max-h-[85vh] overflow-hidden transform transition-all duration-300 scale-100 animate-slide-up border border-gray-700">
        <div className="flex items-center justify-between border-b border-gray-700 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-900/20 rounded-lg">
              <Building2 className="text-red-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Edit External Vendors</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {mapping.registryDisplayName || normalizedPackageIdentifier || 'Package Mapping'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-full hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            <X className="text-gray-400 hover:text-white" size={20} />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto max-h-[calc(85vh-190px)] scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 pr-2">
          <div className="bg-[#252525] rounded-lg p-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Package Identifier:</span>
                <p className="text-red-400 font-mono text-xs mt-1 break-all">{normalizedPackageIdentifier || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Display Name:</span>
                <p className="text-white font-medium mt-1">{mapping.registryDisplayName || '-'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-[#252525] rounded-lg p-3 border border-gray-700">
            <div className="flex items-center gap-2">
              <Link2 className="text-gray-400" size={16} />
              <span className="text-sm text-gray-300">Set external vendors as null</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSendAsNull((prev) => !prev);
                setError('');
              }}
              disabled={saving}
              className={`px-3 py-1 rounded text-xs transition-all ${sendAsNull ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              {sendAsNull ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {!sendAsNull && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300">External Vendors</h3>
                <button
                  type="button"
                  onClick={addVendorRow}
                  disabled={saving}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs text-white flex items-center gap-1 transition-all"
                >
                  <Plus size={14} />
                  Add Row
                </button>
              </div>

              {externalVendors.map((entry, index) => (
                <div key={`vendor-row-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <input
                    type="text"
                    value={entry.vendorName}
                    onChange={(e) => updateVendor(index, 'vendorName', e.target.value)}
                    placeholder="Vendor Name"
                    disabled={saving}
                    className="w-full px-4 py-2.5 bg-[#2a2a2a] text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all disabled:opacity-50"
                  />
                  <input
                    type="text"
                    value={entry.softwareName}
                    onChange={(e) => updateVendor(index, 'softwareName', e.target.value)}
                    placeholder="Software Name"
                    disabled={saving}
                    className="w-full px-4 py-2.5 bg-[#2a2a2a] text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => removeVendorRow(index)}
                    disabled={saving}
                    className="p-2.5 rounded-lg bg-gray-700 hover:bg-red-900/30 hover:border-red-500 border border-transparent transition-all"
                    title="Remove row"
                  >
                    <Trash2 className="text-gray-300" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !normalizedPackageIdentifier}
            className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-red-500/20 font-medium"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditExternalVendorsModal;
